import { useEffect, useRef, useState, type RefObject } from 'react';
import type { ScreenBundle } from '@noorview/core';
import { autoKhutbahNow, indexTimetable, isBroadcastLive, normalizeKhutbah, normalizeMasjid } from '@noorview/core';
import type { Stage } from '@noorview/display';
import { backend } from '../data/store';
import { KhutbahListener, type ListenStatus } from './khutbahListener';

/**
 * Live Khutbah on a screen: shows what's said (from the live feed) while a khutbah is on for this screen, and on the
 * screen chosen to listen, hears the imam and sends each sentence to be translated. That screen also starts the
 * khutbah by itself at Jumu'ah, when the masjid has asked for that.
 */
export function useKhutbah(bundle: ScreenBundle | null, stage: RefObject<Stage | null>) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 5000); return () => clearInterval(t); }, []);
  const live = bundle?.broadcasts?.find((b) => b.kind === 'khutbah' && isBroadcastLive(b, new Date(now))) ?? null;
  const screenId = bundle?.screen.id;
  const listening = !!live && live.khutbah?.listenerScreenId === screenId && !!backend.device.listen;
  const lastSeq = useRef(0);
  const [ready, setReady] = useState(false);
  const [status, setStatus] = useState<{ s: ListenStatus; detail?: string } | null>(null);

  // What's been said: to the screen as it comes.
  useEffect(() => {
    lastSeq.current = 0;
    setReady(false);
    if (!live) return;
    const t = setTimeout(() => setReady(true), 2500);
    const stop = backend.device.watchKhutbah(live.id, (lines) => {
      for (const l of lines) lastSeq.current = Math.max(lastSeq.current, l.seq);
      stage.current?.setKhutbahLines(lines);
      setReady(true);
    });
    return () => { clearTimeout(t); stop(); };
  }, [live?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // The listening screen: hear the imam. Waits for the lines so far, so a restarted screen carries on numbering.
  useEffect(() => {
    if (!listening || !ready || !backend.device.listen) { setStatus(null); return; }
    const l = new KhutbahListener(backend.device.listen, (s, detail) => setStatus({ s, detail }), lastSeq.current, live?.id);
    void l.start();
    return () => l.stop();
  }, [listening, ready, live?.id]);

  // Starting by itself at Jumu'ah (the listening screen only; the database checks it too, and starts each one once).
  const tried = useRef(new Set<string>());
  useEffect(() => {
    if (!bundle || !backend.device.listen || live) return;
    const masjid = normalizeMasjid(bundle.masjid);
    const settings = normalizeKhutbah(bundle.masjid.khutbah);
    if (!bundle.masjid.khutbahAddon || !settings.auto.enabled || settings.listenerScreenId !== bundle.screen.id) return;
    const w = autoKhutbahNow(masjid, indexTimetable(bundle.timetable), settings, new Date(now));
    if (!w) return;
    const key = w.slot.toISOString();
    if (tried.current.has(key)) return;
    tried.current.add(key);
    void backend.device.listen.autoStart(w.slot, w.ends).catch((e) => console.warn('[khutbah] couldn’t start by itself', e));
  }, [bundle, now, live]);

  return listening ? status : null;
}

/** What the listening screen says about itself (small, bottom left; louder when something needs fixing). */
export function listenLabel(s: { s: ListenStatus; detail?: string } | null): { text: string; bad: boolean } | null {
  if (!s) return null;
  switch (s.s) {
    case 'starting': return { text: 'Live khutbah: starting the microphone…', bad: false };
    case 'listening': return { text: 'Live khutbah: listening', bad: false };
    case 'mic-blocked': return { text: 'Live khutbah: this screen can’t use the microphone. On a TV, update NoorView TV (1.1 or newer) and choose Allow when it asks; in a browser, allow the microphone for NoorView.', bad: true };
    case 'no-mic': return { text: 'Live khutbah: no microphone found. Plug in the microphone or the mixer’s cable.', bad: true };
    case 'offline': return { text: `Live khutbah: can’t reach the speech-to-text service${s.detail ? ` (${s.detail})` : ''}. Trying again…`, bad: true };
    case 'stopped': return null;
  }
}
